<template>
  <div>
    <div class="row">
      <!-- Uploader Profile Info -->
      <div class="col-12">
        <div class="form-group form-group-last">
          <div class="alert alert-secondary" role="alert">
            <div class="alert-icon">
              <i class="flaticon-warning kt-font-brand" />
            </div>
            <div class="alert-text">
              Uploader Profiles are used to automate item creation from files uploaded by uploader users.
            </div>
          </div>
        </div>
      </div>

      <!-- Placeholders Description -->
      <div class="col-12">
        <div class="form-group form-group-last">
          <div class="alert alert-secondary" role="alert">
            <div class="alert-text">
              <p class="kt-font-boldest">
                <u>Placeholders</u>
              </p>
              <span v-for="(placeholder, index) in placeholders" :key="index">
                <span class="kt-font-bold">[{{ placeholder.name }}]</span> : {{ placeholder.description }} <br>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <!-- Form Fields -->
      <div class="col-12">
        <!-- Profile Name -->
        <div class="form-group">
          <label>Profile Name</label>
          <input v-model="name" type="text"
                 class="form-control"
                 placeholder="">
          <span class="form-text text-muted">Name this profile so it can be identified later.</span>
        </div>

        <div class="row">
          <div class="col-12 col-md-6 col-lg-8">
            <!-- Name and Description Structure -->
            <div class="form-group">
              <label>Item Name Structure</label>
              <input v-model="item_name_structure" type="text"
                     class="form-control"
                     placeholder="">
              <span class="form-text text-muted">Use placeholders for more dynamic names : 'Morning Show : [sanitised_file_name] - [date]'</span>
            </div>
            <div class="form-group">
              <label>Item Description Structure</label>
              <input v-model="item_description_structure" type="text"
                     class="form-control"
                     placeholder="">
              <span class="form-text text-muted">Morning show presented by John Doe. Aired on [date] and published by [customer] </span>
            </div>
            <!-- Tags -->
            <div class="form-group">
              <div>
                <label for="item_tags">Item Tags</label>
                <multiselect
                  id="item_tags"
                  v-model="ms_tag_values"
                  :options="auto_complete_tags"
                  :multiple="true"
                  :taggable="true"
                  track-by="uuid"
                  label="name"
                  placeholder="Search or add new tags..."
                  @tag="addNewTag" />
                <span class="form-text text-muted">These tags will be added to the item when it's created. This can be used to trigger adding the item to playlists.</span>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6 col-lg-4">
            <!-- Artwork Selection -->
            <display-with-selection-modal :preselected-artwork="selected_artwork" @selected="(ev) => (selected_artwork = ev.selected_artwork)" />
          </div>
        </div>

        <!-- Approval Toggle -->
        <div class="form-group">
          <div class="form-group row">
            <label class="col-4 col-form-label">Approval Required</label>
            <div class="col-3">
              <span class="kt-switch">
                <label>
                  <input v-model="approval_required" type="checkbox" checked="checked">
                  <span />
                </label>
              </span>
            </div>
            <span class="form-text text-muted">If approval is required, the uploaded assets will be stored in a different location and not converted to items until approved by a normal user.</span>
          </div>
        </div>

        <!-- Published toggle -->
        <div class="form-group">
          <div class="form-group row">
            <label class="col-4 col-form-label">Published</label>
            <div class="col-3">
              <span class="kt-switch">
                <label>
                  <input v-model="published" type="checkbox" checked="checked">
                  <span />
                </label>
              </span>
            </div>
          </div>
        </div>

        <!-- Publish Delay -->
        <div v-if="! published" class="form-group">
          <label><strong>Publish Delay</strong></label>
          <div class="row">
            <div class="col-2">
              <label>Days</label>
              <input id="publish_delay_days" v-model.number="publish_delay.days"
                     class="form-control"
                     type="number">
            </div>
            <div class="col-2">
              <label>Hours</label>
              <input id="publish_delay_hours" v-model.number="publish_delay.hours"
                     class="form-control"
                     type="number" max="23">
            </div>
            <div class="col-2">
              <label>Minutes</label>
              <input id="publish_delay_minutes" v-model.number="publish_delay.minutes"
                     class="form-control"
                     type="number" max="59">
            </div>
          </div>
        </div>

        <!-- Publish Duration -->
        <div class="form-group">
          <label><strong>Publish Duration</strong></label>
          <div class="row">
            <div class="col-2">
              <label>Days</label>
              <input id="publish_duration_days" v-model.number="publish_duration.days"
                     class="form-control"
                     type="number"
                     min="0">
            </div>
            <div class="col-2">
              <label>Hours</label>
              <input id="publish_duration_hours" v-model.number="publish_duration.hours"
                     class="form-control"
                     type="number" max="23"
                     min="0">
            </div>
            <div class="col-2">
              <label>Minutes</label>
              <input id="publish_duration_minutes" v-model.number="publish_duration.minutes"
                     class="form-control"
                     type="number" max="59"
                     min="0">
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Uploader Users -->
    <div class="form-group">
      <div>
        <label for="item_tags">Uploader Users</label>
        <multiselect
          id="uploader_users"
          v-model="uploaderUser"
          :options="uploaderUsers"
          :multiple="true"
          :taggable="true"
          track-by="id"
          label="name"
          placeholder="Select a user to assign to..." />
        <span class="form-text text-muted">One or multiple uploader users can be assigned to this profile. Any already assigned users will be replaced. To unassign or assign later, leave the input blank.</span>
      </div>
    </div>

    <div class="row">
      <!-- Item Example -->
      <div class="col-12">
        <div class="form-group form-group-last">
          <div class="alert alert-secondary" role="alert">
            <div class="alert-text">
              <h5>Example Output</h5>
              <uploader-profile-example :uploader-profile="the_profile" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12">
      <div class="form-group form-group-last">
        <button type="button" class="btn btn-ss-standout" @click="saveUploaderProfile()">
          Save Profile
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import DisplayWithSelectionModal from "VuePartials/Artwork/DisplayWithSelectionModal";
import UploaderProfileExample from "VuePartials/App/ThirdPartyUploader/UploaderProfileExample";
import UploaderProfilesMixin from "VueMixins/UploaderProfilesMixin";
import UsersMixin from "VueMixins/UsersMixin";
import TaggableModelMixin from 'VueMixins/TaggableModelMixin';

export default {
  components: { DisplayWithSelectionModal, UploaderProfileExample },
  mixins: [TaggableModelMixin, UploaderProfilesMixin, UsersMixin],
  props: {
    uploaderId: {
      type: Number,
      required: false,
      default: 0,
    },
    uploaderProfile: {
      type: Object,
      required: false,
      default: function () {
        return {};
      },
    },
    modalName: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      uploaderUser: null,
      example_file_name: "john_doe_breakfast_show_01-01-2019.mp3",
      edit_id: null,
      name: null,
      published: false,
      item_name_structure: null,
      item_description_structure: null,
      file_name_sanitisation_options: {
        capitalise: true,
      },
      publish_delay: {
        days: 0,
        hours: 0,
        minutes: 0,
      },
      publish_duration: {
        days: 0,
        hours: 0,
        minutes: 0,
      },
      approval_required: false,
      selected_artwork: null,
      placeholders: [
        {
          name: 'sanitised_file_name',
          description: 'The file name stripped of all but text characters',
        },
        {
          name: 'date',
          description: 'Date the file was added : 01/01/2020',
        },
        {
          name: 'datetime',
          description: 'Date and time the file was added : 01/01/2020 08:20:26',
        },
        {
          name: 'customer',
          description: `Your customer name '${window.user.customer.name}'`,
        },
      ],
    };
  },
  computed: {
    the_profile() {
      return {
        name: this.name,
        published: this.published,
        approval_required: this.approval_required,
        settings: {
          item_name_structure: this.item_name_structure,
          item_description_structure: this.item_name_structure,
          publish_delay: this.publish_delay,
          publish_duration: this.publish_duration,
          tags: this.ms_tag_values,
        },
        artwork: this.selected_artwork,
      };
    },
    has_publish_delay() {
      if (this.published) {
        return false;
      }
      return this.publish_delay.days || this.publish_delay.hours || this.publish_delay.minutes;
    },
    has_publish_duration() {
      return this.publish_duration.days || this.publish_duration.hours || this.publish_duration.minutes;
    },
  },
  watch: {
    published: function (new_val) {
      // If a publish delay is not required, then reset the values.
      if (new_val) {
        this.publish_delay.days = 0;
        this.publish_delay.hours = 0;
        this.publish_delay.minutes = 0;
      }
    },
  },
  beforeMount() {
    this.getUploaderUsers();
    this.getTags();

    if (this.uploaderProfile) {
      this.edit_id = this.uploaderProfile.id;
      this.name = this.uploaderProfile.name;
      if (this.uploaderProfile.settings?.item_name_structure) {
        this.item_name_structure = this.uploaderProfile.settings.item_name_structure;
      }

      if (this.uploaderProfile.settings?.item_description_structure) {
        this.item_description_structure = this.uploaderProfile.settings.item_description_structure;
      }

      this.approval_required = this.uploaderProfile.approval_required;

      if (this.uploaderProfile.settings?.published) {
        this.published = this.uploaderProfile.settings.published;
      }

      if (this.uploaderProfile.settings?.publish_duration) {
        this.publish_duration = this.convertSecondsToDaysHoursMinutes(this.uploaderProfile.settings.publish_duration);
      }

      if (this.uploaderProfile.settings?.publish_delay) {
        this.publish_delay = this.convertSecondsToDaysHoursMinutes(this.uploaderProfile.settings.publish_delay);
      }

      if (this.uploaderProfile.artwork_id) {
        this.selected_artwork = this.uploaderProfile.artwork;
      }

      if (this.uploaderProfile.users) {
        this.uploaderUser = this.uploaderProfile.users;
      }
    }
  },
  mounted() {
    EventBus.$on('new_artwork_selected', (payload) => {
      this.selected_artwork = payload;
    });
  },
  methods: {
    async getTags() {
      try {
        this.auto_complete_tags = (await axios.get(`/api/v1/tags`)).data.data.map(tag => tag.name).sort();
        if (this.uploaderProfile?.settings?.tags) {
          this.ms_tag_values = this.uploaderProfile.settings?.tags;
        }
      } catch (err) {
        console.error(err);
        window.handleAxiosError(err);
      }
    },
    saveUploaderProfile() {
      const data = {
        name: this.name,
        item_name_structure: this.item_name_structure,
        item_description_structure: this.item_description_structure,
        approval_required: this.approval_required,
        tags: this.ms_tag_values,
        published: this.published,
        uploaderUsers: this.uploaderUser,
      };

      if (this.selected_artwork) {
        data.artwork_uuid = this.selected_artwork.uuid;
      }

      if (this.has_publish_delay) {
        data.publish_delay = this.convertPublishDataToSeconds(this.publish_delay);
      }

      if (this.has_publish_duration) {
        data.publish_duration = this.convertPublishDataToSeconds(this.publish_duration);
      }

      if (this.edit_id) {
        data.uploader_profile_id = this.edit_id;
      }

      axios.post(`/api/v1/uploader-profiles`, data).then((response) => {
        toastr.success("Profile Saved");
        window.EventBus.$emit("uploader-profile-added", response.data.data);
        window.EventBus.$emit('uploader-profiles-table-reload');
        if (this.modalName) this.$modal.hide(this.modalName);
      }).catch((error) => {
        window.handleAxiosError(error);
      });
    },
  },
};
</script>
